import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatRupiah } from "@/lib/format";
import { Receipt, Loader2, CheckCircle2 } from "lucide-react";
import { AdminActions, AdminDeleteButton } from "@/components/AdminActions";
import { useBulk, BulkCheck, BulkBar } from "@/components/BulkDelete";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAutoRefresh } from "@/lib/autoRefresh";

export default function VehiclePayables() {
  const { user } = useAuth();
  const canPay = ["direktur", "tim_keuangan"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("unpaid");
  const [busyId, setBusyId] = useState(null);
  const bulk = useBulk();

  const load = useCallback((silent) => {
    if (silent !== true) setLoading(true);
    api.get("/vehicle-payables").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const pay = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/vehicle-payables/${id}/pay`);
      toast.success("Tagihan ditandai sudah dibayar.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const filtered = rows.filter((r) => r.status === tab);
  const unpaidTotal = rows.filter((r) => r.status === "unpaid").reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="vehicle-payables-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Receipt className="h-7 w-7 text-brand" /> Tagihan Kendaraan
        </h1>
        <p className="text-muted-foreground mt-1">Tagihan tempo pengerjaan kendaraan (Tim Kendaraan Pusat) yang harus dibayar.</p>
      </div>

      <div className="rounded-lg bg-secondary/60 border p-4 flex items-center justify-between" data-testid="vehicle-payables-summary">
        <span className="text-sm text-muted-foreground">Total Belum Dibayar</span>
        <span className="text-2xl font-bold text-rose-600 font-mono" style={{ fontFamily: "Outfit" }}>{formatRupiah(unpaidTotal)}</span>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="unpaid" data-testid="tab-unpaid">Belum Dibayar</TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-paid">Sudah Dibayar</TabsTrigger>
        </TabsList>
      </Tabs>

      <BulkBar bulk={bulk} onDone={load} label="tagihan kendaraan" />
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">Tidak ada tagihan {tab === "unpaid" ? "belum dibayar" : "yang sudah dibayar"}.</Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <Card key={r.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`payable-${r.id}`}>
              <div className="min-w-0 flex items-start gap-3">
                <span className="pt-1"><BulkCheck bulk={bulk} k={r.id} item={{ kind: "vehicle-payables", id: r.id }} testId={`bulk-payable-${r.id}`} /></span>
                <div className="min-w-0">
                <p className="font-semibold" style={{ fontFamily: "Outfit" }}>{r.plate_number} · {r.branch}</p>
                <p className="text-xs text-muted-foreground">{(r.items || []).join(", ") || "Pengerjaan"} · {formatDate(r.date)} · oleh {r.created_by}</p>
                {r.note && <p className="text-xs text-muted-foreground mt-0.5">{r.note}</p>}
                {r.status === "paid" && <p className="text-[11px] text-emerald-600 mt-0.5">Dibayar {formatDate(r.paid_at)} oleh {r.paid_by}</p>}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-mono font-bold text-brand" data-testid={`payable-amount-${r.id}`}>{formatRupiah(r.amount)}</span>
                {r.status === "paid" && <AdminDeleteButton path={`/admin/reset/payable-payment/${r.id}`} onDone={load} label="Hapus Pembayaran" testId={`admin-reset-payable-${r.id}`} confirmText="Pembayaran dihapus permanen. Tagihan kembali ke Belum Dibayar." />}
                <AdminActions editPath={`/admin/records/vehicle-payables/${r.id}`} deletePath={`/admin/records/vehicle-payables/${r.id}`} fields={[{ k: "date", l: "Tanggal", t: "date" }, { k: "amount", l: "Nominal", t: "money" }, { k: "note", l: "Catatan" }]} values={r} onDone={load} title="Edit Tagihan Kendaraan" testId={`admin-payable-${r.id}`} />
                {r.status === "unpaid" && canPay && (
                  <Button size="sm" disabled={busyId === r.id} onClick={() => pay(r.id)} className="bg-brand hover:bg-emerald-800" data-testid={`pay-btn-${r.id}`}>
                    {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Tandai Dibayar</>}
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
